import { FC, useState, ReactNode, useEffect } from 'react';
import { MobileStepper, Button, Box, Typography, Fade, Stack } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { Content as Wrapper } from '../content/content.component.tsx';
import { stepperState } from './states/stepper.state.ts';
import { useTranslation } from 'react-i18next';

export const Stepper: FC<IStepper> = ({ steps, visibleDefaultControls = true }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [valid, setValid] = useState(false);
  useEffect(() => {
    const stepperStateSub = stepperState.sub.state().subscribe((state) => {
      setActiveStep(state.activeStep);
      setValid(state.valid);
    });
    return () => {
      stepperState.pub.resetData();
      stepperStateSub.unsubscribe();
    };
  }, []);

  const maxSteps = steps.length;

  const handleNext = () => {
    if (activeStep !== maxSteps - 1) {
      stepperState.pub.nextStep({ activeStep, valid });
    }
  };
  const handlePrev = () => stepperState.pub.prevStep({ activeStep });

  return (
    <Wrapper type='stack' stackProps={{ flexGrow: 1, justifyContent: 'space-between' }}>
      <Fade key={activeStep} in timeout={700} mountOnEnter unmountOnExit>
        <Stack>
          {steps[activeStep]?.title && (
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                fontSize: 24,
                marginBottom: 2,
                lineHeight: 1.1,
              }}
            >
              {steps[activeStep].title}
            </Typography>
          )}
          {steps[activeStep]?.subtitle && (
            <Typography
              variant='caption'
              sx={{
                fontWeight: 400,
                fontSize: 14,
                marginBottom: 3,
              }}
            >
              {steps[activeStep].subtitle}
            </Typography>
          )}
          <Box sx={{ flexGrow: 1 }}>{steps[activeStep].Component}</Box>
        </Stack>
      </Fade>
      {visibleDefaultControls && (
        <MobileStepper
          variant='text'
          steps={maxSteps}
          position='static'
          sx={{ marginTop: 4, p: 0 }}
          activeStep={activeStep}
          nextButton={
            activeStep !== maxSteps - 1 ? (
              <Button
                variant='contained'
                disabled={!valid}
                onClick={handleNext}
                endIcon={<KeyboardArrowRight />}
              >
                {t('common.next')}
              </Button>
            ) : (
              <Button
                disabled={!valid}
                variant='contained'
                onClick={() => stepperState.pub.runFinishFn()}
              >
                {steps[activeStep]?.finalButtonName || t('common.submit')}
              </Button>
            )
          }
          backButton={
            <Button
              onClick={handlePrev}
              startIcon={<KeyboardArrowLeft />}
              sx={{ ...(activeStep === 0 && { visibility: 'hidden' }) }}
            >
              {t('common.back')}
            </Button>
          }
        />
      )}
    </Wrapper>
  );
};

export interface IStepper {
  steps: Array<{
    Component: ReactNode;
    title?: string;
    subtitle?: string;
    finalButtonName?: string;
  }>;
  visibleDefaultControls?: boolean;
}
